import { useCallback, useEffect, useState } from "react";
import { useTheme, Typography, Button } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { Box } from "@mui/system";
import { Page } from "@mb-pro-ui/components";
import { useIntl } from "react-intl";

import TitledCard from "./templates/basic/TitledCard";
import { useDevice } from "../utils/DeviceProvider";
import TemplateLoading from "./templates/TemplateLoading";
import BasicTemplate from "./templates/BasicTemplate";

import { SmartalarmProvider } from "./templates/basic/smartalarm/SmartalarmTemplate";
import { ThermowatchProvider } from "./templates/basic/thermowatch/ThermowatchTemplate";
import { usePageSxProps } from "../pages/styles";
import { useSnackbar } from "notistack";
import { snackbarOptions } from "./snackbar";
import { useHistory } from "react-router-dom";

const TemplateContainer = () => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { device, init, updateUnitDetails, updateTemplate, updateConfig, setCurrentValue } = useDevice();
  const { unitid, unknownDevice, primaryUnitid, template, config, noConfig, currentValue } = device || {};

  const theme = useTheme();
  const { singlePageSx } = usePageSxProps();
  const { formatMessage } = useIntl();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const history = useHistory();

  const navigateToPrimary = useCallback(() => {
    if (!primaryUnitid) {
      return;
    }
    history.push(`/configuration/${primaryUnitid}`);
  }, [history, primaryUnitid]);

  useEffect(() => {
    if (!device) {
      init();
    }
  }, [device, init]);

  useEffect(() => {
    if (unknownDevice === null) {
      updateUnitDetails();
    }
  }, [unknownDevice, updateUnitDetails]);

  useEffect(() => {
    if (unknownDevice === false && !template) {
      updateTemplate().catch((err) => {
        if (err instanceof SyntaxError) {
          setErrorMessage(null);
        } else {
          setErrorMessage(err.message);
        }
      });
    }
  }, [unknownDevice, template, updateTemplate, unitid]);

  useEffect(() => {
    if (template && !config && !noConfig) {
      try {
        if (template.template) {
          updateConfig().then(() => {
            const loadedConfig = localStorage.getItem(`loaded-config-${unitid}`);
            if (loadedConfig) {
              setCurrentValue(JSON.parse(loadedConfig));
              localStorage.removeItem(`loaded-config-${unitid}`);
              enqueueSnackbar(formatMessage({ defaultMessage: "Config loaded." }), snackbarOptions({ variant: "success" }, { closeSnackbar }));
            }
          });
        }
        setErrorMessage(null);
      } catch (err: any) {
        setErrorMessage(err.message);
      }
    }
  }, [template, config, noConfig, currentValue, unitid, updateConfig, formatMessage, setCurrentValue, enqueueSnackbar, closeSnackbar]);

  if (!device) {
    return null;
  }

  if (!unknownDevice && !template && !errorMessage) {
    return <TemplateLoading />;
  }

  if (!unknownDevice && !errorMessage && template?.template) {
    return (
      <SmartalarmProvider>
        <ThermowatchProvider>
          <BasicTemplate />
        </ThermowatchProvider>
      </SmartalarmProvider>
    );
  }
  return (
    <Page sx={singlePageSx}>
      {!!primaryUnitid ? (
        <TitledCard
          title={formatMessage({ defaultMessage: "Secondary SIM" })}
          color={theme.palette.secondary.main}
          contrastColor={theme.palette.secondary.contrastText}
        >
          <Box sx={{ padding: 2 }}>
            <Typography variant="h5" color="primary">
              #{unitid}
            </Typography>
            <Typography>
              {formatMessage(
                { defaultMessage: "Helios ID {unitid} is for a secondary SIM card. You can configure this device via Helios ID {primaryUnitid}." },
                { unitid, primaryUnitid }
              )}
            </Typography>
            <Button onClick={navigateToPrimary}>{formatMessage({ defaultMessage: "Navigate to {unitid}" }, { unitid: primaryUnitid })}</Button>
          </Box>
        </TitledCard>
      ) : unknownDevice ? (
        <TitledCard
          title={formatMessage({ defaultMessage: "Unknown device" })}
          color={theme.palette.secondary.main}
          contrastColor={theme.palette.secondary.contrastText}
        >
          <Box sx={{ padding: 2 }}>
            <Typography variant="h5" color="primary">
              #{unitid}
            </Typography>
            <Typography>{formatMessage({ defaultMessage: "Device does not exist or does not have permission to configure." })}</Typography>
          </Box>
        </TitledCard>
      ) : !template?.template ? (
        <TitledCard
          title={formatMessage({ defaultMessage: "No template" })}
          color={theme.palette.secondary.main}
          contrastColor={theme.palette.secondary.contrastText}
        >
          <Box sx={{ padding: 2 }}>
            <Typography variant="h5" color="primary">
              #{unitid}
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "row", flexWrap: "wrap", alignItems: "center" }}>
              <Typography sx={{ marginRight: 1 }}>{formatMessage({ defaultMessage: "Device does not have any template." })}</Typography>
            </Box>
          </Box>
        </TitledCard>
      ) : errorMessage ? (
        <TitledCard
          title={formatMessage({ defaultMessage: "Error" })}
          color={theme.palette.secondary.main}
          contrastColor={theme.palette.secondary.contrastText}
        >
          <Box sx={{ padding: 2 }}>
            <Typography variant="h5" color="primary">
              #{unitid}
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "row", flexWrap: "wrap", alignItems: "center" }}>
              <Typography sx={{ marginRight: 1 }}>
                {errorMessage === "old-template" ? "Régi (ideiglenes, nem kiadott) formátumú template. Firmware frissítés szükséges." : errorMessage}
              </Typography>
            </Box>
          </Box>
        </TitledCard>
      ) : (
        <TitledCard
          title={formatMessage({ defaultMessage: "Error" })}
          color={theme.palette.secondary.main}
          contrastColor={theme.palette.secondary.contrastText}
        >
          <Box sx={{ padding: 2 }}>
            <Typography variant="h5" color="primary">
              #{unitid}
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "row", flexWrap: "wrap", alignItems: "center" }}>
              <Typography sx={{ marginRight: 1 }}>
                {formatMessage({ defaultMessage: "You should not see this. This template looks valid but doesn't have any implementation." })}
              </Typography>
            </Box>
          </Box>
        </TitledCard>
      )}
    </Page>
  );
};

export const useMenuStyles = makeStyles(() => {
  const theme = useTheme();

  return {
    withSeparator: {
      "& .MuiListItem-root": {
        borderTopWidth: 1,
        borderTopColor: theme.palette.divider,
        borderTopStyle: "solid",
      },
    },
  };
});

export default TemplateContainer;
